import type { CSSProperties } from "react";
import styled, { css, type DefaultTheme } from "styled-components";
import { Box, Button, Link } from "@viagogo/core/ui";
import { FavoritesButtonWithCount } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/FavoritesButtonWithCount";
import { Image } from "src/website-apps/shared/components/image";

const bannerHeights = {
  mobile: 260,
  tablet: 376,
  desktop: 432,
};

const mobileBannerHeights = {
  image: 184,
  titles: 76,
};

const addBannerHeight = css`
  height: ${bannerHeights.mobile}px;

  @media ${(props) => props.theme.media.tablet} {
    height: ${bannerHeights.tablet}px;
  }

  @media ${(props) => props.theme.media.desktop} {
    height: ${bannerHeights.desktop}px;
  }
`;

const addBannerMargin = css<{ $narrowTopMargin?: boolean }>`
  margin: ${(props) => props.theme.spacing(1, 4, 0, 4)};

  @media ${(props) => props.theme.media.tablet} {
    margin: ${(props) => props.theme.spacing(props.$narrowTopMargin ? 6 : 8, 4, 0, 4)};
  }

  @media ${(props) => props.theme.media.desktop} {
    margin: ${(props) => props.theme.spacing(props.$narrowTopMargin ? 0 : 8, 8, 0, 8)};
  }

  ${(props) => {
    return props.theme.media.makeMediaQuery(
      1200,
      `
      margin: 0px auto 0px auto;
      width: 1200px;
    `,
    );
  }}
`;

export const StyledClearHeroBanner = styled.div<{ $noBottomPadding?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: ${(props) => (props.$noBottomPadding ? "0px" : props.theme.spacing(4))};
`;

export const HeroContainer = styled.div<{ $narrowTopMargin?: boolean }>`
  display: flex;
  flex-grow: 1;
  max-width: 1200px;

  ${addBannerHeight}
  ${addBannerMargin}
`;

export const EntryDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-self: center;

  height: 100%;
  justify-content: center;

  & > * {
    width: unset;
  }
`;

const gradient = (isStubhub: boolean, isMobile: boolean, theme: DefaultTheme) => {
  const bg1 = isStubhub ? theme.palette.brandSecondary : theme.palette.borderSuccessSecondary;
  const bg2 = "rgba(255,0,0,0)"; // Transparent
  const deg = isMobile ? "to right top" : "to right bottom";
  return `linear-gradient(${deg}, ${bg1}, ${bg1} 49%, ${bg2} 51%);`;
};

const solid = (isStubhub: boolean, theme: DefaultTheme) => {
  return isStubhub ? theme.palette.brandSecondary : theme.palette.borderSuccessSecondary;
};

export const HeroSection = styled(Link)`
  display: grid;
  border-radius: ${(props) => props.theme.borderRadius.xxl};
  width: 100%;

  grid-template-rows: auto 36%;
  grid-template-columns: unset;
  grid-template-areas: "image" "text";
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    grid-template-columns: 36% auto;
    grid-template-areas: "text image";
  }
`;

const addBorderRadius = (
  ne: CSSProperties["borderTopRightRadius"],
  se: CSSProperties["borderBottomRightRadius"],
  sw: CSSProperties["borderBottomLeftRadius"],
  nw: CSSProperties["borderTopLeftRadius"],
) => css`
  border-top-right-radius: ${ne};
  border-bottom-right-radius: ${se};
  border-bottom-left-radius: ${sw};
  border-top-left-radius: ${nw};
`;

export const HeroSectionText = styled.div<{ isStubhub?: boolean }>`
  color: ${(props) => props.theme.palette.textPrimaryOnSurface};
  background: ${({ isStubhub, theme }) => solid(isStubhub ?? false, theme)};
  grid-area: text;
  display: grid;
  grid: 1fr auto / 1fr;
  gap: ${(props) => props.theme.spacing(4)};
  word-break: break-word;

  ${({ theme }) => addBorderRadius(0, theme.borderRadius.xl, theme.borderRadius.xl, 0)}
  height: ${() => mobileBannerHeights.titles}px;
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    ${({ theme }) => addBorderRadius(0, 0, theme.borderRadius.xxl, theme.borderRadius.xxl)}
    ${addBannerHeight}
  }

  padding: ${(props) => props.theme.spacing(0, 4)};
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    padding: ${(props) => props.theme.spacing(8, 3, 8, 8)};
  }
`;

export const BannerEyebrow = styled.h3<{ bold?: boolean }>`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  margin-bottom: ${(props) => props.theme.spacing(1)};
  text-transform: uppercase;

  ${({
    bold,
    theme: {
      typography: { getStyles, variant, fontWeight },
    },
  }) => getStyles(variant.title6, bold ? fontWeight.bold : fontWeight.regular)};
`;

export const BannerTitle = styled.h2<{ $withSubtitle?: boolean }>`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  margin-bottom: 0px; // No margin in mobile
  @media ${(props) => `${props.theme.media.tablet}, ${props.theme.media.desktop}`} {
    margin-bottom: ${(props) => (props.$withSubtitle ? props.theme.spacing(2) : props.theme.spacing(4))};
  }

  ${({
    theme: {
      typography: { getStyles, variant },
    },
  }) => getStyles(variant.title5)};

  @media ${(props) => props.theme.media.tablet} {
    ${({
      theme: {
        typography: { getStyles, variant },
      },
    }) => getStyles(variant.title3)};
    -webkit-line-clamp: 3;
  }

  @media ${(props) => props.theme.media.desktop} {
    ${({
      theme: {
        typography: { getStyles, variant },
      },
    }) => getStyles(variant.title1)};
    -webkit-line-clamp: 3;
  }
`;

export const BannerSubTitle = styled.h4`
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  opacity: 85%;
  margin-bottom: ${(props) => props.theme.spacing(4)};

  ${({
    theme: {
      typography: { getStyles, variant, fontWeight },
    },
  }) => getStyles(variant.title5, fontWeight.medium)};
`;

export const StyledButton = styled(Button)`
  border-radius: ${(props) => props.theme.borderRadius.l};
  padding: ${(props) => props.theme.spacing(3.5, 4)};
  font-size: 14px;
  line-height: 20px;
`;

export const HeroSectionImage = styled.div`
  position: relative;
  grid-area: image;

  ${({ theme }) => addBorderRadius(theme.borderRadius.xl, 0, 0, theme.borderRadius.xl)}
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    ${({ theme }) => addBorderRadius(theme.borderRadius.xxl, theme.borderRadius.xxl, 0, 0)}
  }
`;

export const StyledImage = styled(Image)`
  display: block;
  width: 100%;
  object-fit: cover;

  ${({ theme }) => addBorderRadius(theme.borderRadius.xl, 0, 0, theme.borderRadius.xl)}
  height: ${() => mobileBannerHeights.image}px;
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    ${({ theme }) => addBorderRadius(theme.borderRadius.xxl, theme.borderRadius.xxl, 0, 0)}
    ${addBannerHeight}
  }
`;

export const HeroSectionDivider = styled.div<{ isStubhub?: boolean; isMobile?: boolean }>`
  position: absolute;
  left: 0px;
  bottom: 0px;

  width: auto;
  height: 13px;
  right: 0px;
  @media ${({ theme }) => `${theme.media.tablet}, ${theme.media.desktop}`} {
    width: 32px;
    ${addBannerHeight}
    right: unset;
  }

  background: ${({ isStubhub, isMobile, theme }) => gradient(isStubhub ?? false, isMobile ?? false, theme)};
`;

export const StyledFavoritesButton = styled(FavoritesButtonWithCount)`
  position: absolute;
  top: ${(props) => props.theme.spacing(4)};
  right: ${(props) => props.theme.spacing(4)};
`;

export const CTAContainer = styled(Box)``;

export const MobilePagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing(2)};
`;
